import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import { RESOURCE_BUSY_EVENT } from '../../services/busyNotice';

import './Toast.css';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastAction {
  label: string;
  href: string;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  actions?: ToastAction[];
  duration: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, actions?: ToastAction[], duration?: number) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string, actions?: ToastAction[]) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

const DEFAULT_DURATION_MS = 6000;
const ACTION_DURATION_MS = 60000;
const EXIT_LEAD_MS = 300;

const useToastManager = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', actions?: ToastAction[], duration?: number) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const ms = duration ?? (actions && actions.length > 0 ? ACTION_DURATION_MS : DEFAULT_DURATION_MS);

      // The same text already on screen (e.g. a global notice and the caller's own toast) shows once.
      setToasts((prev) => (
        prev.some((toast) => toast.message === message) ? prev : [...prev, { id, message, type, actions, duration: ms }]
      ));

      setTimeout(() => removeToast(id), ms);
    },
    [removeToast]
  );

  return { toasts, removeToast, showToast };
};

const ToastContainer: React.FC<{
  toasts: Toast[];
  onRemove: (id: string) => void;
}> = ({ toasts, onRemove }) => (
  <div className="toast-container">
    {toasts.map((toast) => (
      <ToastItem key={toast.id} toast={toast} onClose={() => onRemove(toast.id)} />
    ))}
  </div>
);

const useToastHelpers = (showToast: (message: string, type?: ToastType, actions?: ToastAction[]) => void) => {
  const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
  const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);
  const warning = useCallback(
    (message: string, actions?: ToastAction[]) => showToast(message, 'warning', actions),
    [showToast]
  );

  return { success, error, info, warning };
};

/** A request answered "busy, try again in a moment" is shown even when its caller stays silent. */
const useResourceBusyToasts = (showToast: (message: string, type?: ToastType) => void) => {
  useEffect(() => {
    const handler = (event: Event) => {
      const message = (event as globalThis.CustomEvent<{ message?: string }>).detail?.message;

      if (message) showToast(message, 'warning');
    };

    window.addEventListener(RESOURCE_BUSY_EVENT, handler);

    return () => window.removeEventListener(RESOURCE_BUSY_EVENT, handler);
  }, [showToast]);
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { toasts, removeToast, showToast } = useToastManager();
  const { success, error, info, warning } = useToastHelpers(showToast);

  useResourceBusyToasts(showToast);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsExiting(true), toast.duration - EXIT_LEAD_MS);

    return () => clearTimeout(timer);
  }, [toast.duration]);

  const icons: Record<ToastType, string> = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div className={`toast toast--${toast.type} ${isExiting ? 'toast--exiting' : ''}`}>
      <span className="toast__icon">{icons[toast.type]}</span>
      <div className="toast__body">
        <span className="toast__message">{toast.message}</span>
        {toast.actions && toast.actions.length > 0 && (
          <div className="toast__actions">
            {toast.actions.map((action) => (
              <a key={action.label} href={action.href} className="toast__action-btn">
                {action.label}
              </a>
            ))}
          </div>
        )}
      </div>
      <button className="toast__close" onClick={onClose}>
        &times;
      </button>
    </div>
  );
};
